import { config } from "./config";

// Cognito CUSTOM_AUTH（パスワードレス email + 6桁コード / C-3-2）クライアント。
// バックエンド: apps/workers/src/auth-challenge.ts（Define/Create/Verify トリガー）。
// フロー: InitiateAuth(CUSTOM_AUTH) → メール送信 → RespondToAuthChallenge(CUSTOM_CHALLENGE)。
// guide-app（src/lib/cognito.ts）と同一の検証済みフローを共有する（将来 packages/auth へ抽出）。

const endpoint = () => `https://cognito-idp.${config.cognito.region}.amazonaws.com/`;

class CognitoError extends Error {
  constructor(
    public readonly type: string,
    message: string,
  ) {
    super(message);
    this.name = "CognitoError";
  }
}

async function idp<T>(target: string, payload: unknown): Promise<T> {
  const res = await fetch(endpoint(), {
    method: "POST",
    headers: {
      "content-type": "application/x-amz-json-1.1",
      "x-amz-target": `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(payload),
  });
  const data = (await res.json()) as Record<string, unknown>;
  if (!res.ok) {
    const type = String(data.__type ?? "UnknownError");
    throw new CognitoError(type, String(data.message ?? type));
  }
  return data as T;
}

type InitiateAuthResult = { Session?: string };
type RespondResult = {
  Session?: string;
  AuthenticationResult?: { IdToken: string; AccessToken: string; RefreshToken?: string };
};

/** 認証コードを email 宛に送信し、後続検証用の Session を返す（CUSTOM_CHALLENGE 発行）。 */
export async function requestAuthCode(email: string): Promise<{ session: string }> {
  const data = await idp<InitiateAuthResult>("InitiateAuth", {
    AuthFlow: "CUSTOM_AUTH",
    ClientId: config.cognito.clientId,
    AuthParameters: { USERNAME: email },
  });
  if (!data.Session) {
    // 列挙防止のためバックエンドは常に Session を返す設計。欠落は構成不整合。
    throw new CognitoError("NoSession", "Could not start sign-in. Please try again.");
  }
  return { session: data.Session };
}

export type VerifyOutcome =
  | { status: "success"; idToken: string; accessToken: string }
  | { status: "retry"; session: string }; // コード不一致でチャレンジ継続（新 Session）

/** 入力コードを検証する。成功で IdToken、不一致なら新 Session を返し再入力させる。 */
export async function verifyAuthCode(
  email: string,
  code: string,
  session: string,
): Promise<VerifyOutcome> {
  const data = await idp<RespondResult>("RespondToAuthChallenge", {
    ChallengeName: "CUSTOM_CHALLENGE",
    ClientId: config.cognito.clientId,
    Session: session,
    ChallengeResponses: { USERNAME: email, ANSWER: code },
  });
  if (data.AuthenticationResult?.IdToken) {
    return {
      status: "success",
      idToken: data.AuthenticationResult.IdToken,
      accessToken: data.AuthenticationResult.AccessToken,
    };
  }
  if (data.Session) return { status: "retry", session: data.Session };
  // Session も IdToken も無い = 試行上限/期限切れでチャレンジ終了。
  throw new CognitoError("NotAuthorizedException", "Too many attempts or the code expired.");
}

/** Cognito の例外 __type をユーザー向け文言に対応づける（g-03 のエラー表示）。 */
export function authErrorMessage(err: unknown): string {
  if (err instanceof CognitoError) {
    switch (err.type) {
      case "NotAuthorizedException":
        return "Incorrect or expired code, or too many attempts. Request a new code.";
      case "UserNotFoundException":
        // 列挙防止: バックエンドは存在しない email でも Session を返すため通常は到達しない。
        return "Could not sign you in. Contact TST support if this continues.";
      case "TooManyRequestsException":
      case "LimitExceededException":
        return "Too many requests. Please wait a moment and try again.";
      default:
        return err.message;
    }
  }
  return "Network error. Check your connection and try again.";
}

// ── JWT（IdToken）デコード ─────────────────────────────────────────────
export type GuideClaims = {
  sub: string;
  email?: string;
  name?: string;
  groups: string[];
  exp: number; // epoch 秒
};

/** IdToken のペイロードを検証なしでデコード（表示用途のみ。認可は API GW JWT authorizer が担う）。 */
export function decodeIdToken(idToken: string): GuideClaims | null {
  try {
    const payload = idToken.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const c = JSON.parse(json) as Record<string, unknown>;
    const groups = c["cognito:groups"];
    return {
      sub: String(c.sub ?? ""),
      email: typeof c.email === "string" ? c.email : undefined,
      name: typeof c.name === "string" ? c.name : undefined,
      groups: Array.isArray(groups) ? groups.map(String) : [],
      exp: typeof c.exp === "number" ? c.exp : 0,
    };
  } catch {
    return null;
  }
}
